import {
  assignProgramFn,
  getActiveUserProgramOptions,
} from "@/actions/user-program/handler";
import { formatDaysPerWeekShort } from "@/api/db/schema/training-programs";
import { authClient } from "@/auth/auth-client";
import { trackProgramStart } from "@/components/analytics/conversions";
import { ProgramActivationWizard } from "@/components/program/ProgramActivationWizard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Calendar, Clock, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
interface ProgramCardProps {
  program: {
    programId: string;
    name: string;
    description?: string | null;
    durationWeeks: number;
    daysPerWeek: number;
    maxDaysPerWeek?: number | null;
    difficulty: number;
    image?: string | null;
    accessCode?: string | null;
    isPaid?: boolean | null;
    price?: number | null;
    oldPrice?: number | null;
    requiresPurchase?: boolean;
    hasAccess?: boolean;
    catalogItemId?: string;
    catalogName?: string | null;
    catalogDescription?: string | null;
    priceKurus?: number;
    currency?: string;
  };
  isActive?: boolean;
}

export function ProgramCard({ program, isActive }: ProgramCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();
  const { data: activeData } = useQuery({
    ...getActiveUserProgramOptions(),
    enabled: Boolean(session?.user),
  });
  const activeProgram =
    activeData?.body && "programId" in activeData.body ? activeData.body : null;
  const isCurrentProgram =
    isActive || activeProgram?.programId === program.programId;

  const isPaidProgram = Boolean(
    program.requiresPurchase ||
      program.isPaid ||
      (program.price && program.price > 0) ||
      program.accessCode,
  );

  const hasAccess = program.hasAccess ?? !isPaidProgram;
  const needsPurchase = isPaidProgram && !hasAccess;

  const displayPriceTl = program.priceKurus
    ? program.priceKurus / 100
    : (program.price ?? null);

  const effectiveDescription =
    program.catalogDescription || program.description;
  const effectiveName = program.catalogName || program.name;

  const assignMutation = useMutation({
    mutationFn: assignProgramFn,
    onSuccess: (result) => {
      if (result.status !== 201) {
        toast.error(
          result.body && "message" in result.body
            ? result.body.message
            : "Program başlatılamadı.",
        );
        return;
      }
      trackProgramStart(program.programId, program.name);
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
      navigate({
        to: "/program/$programId",
        params: { programId: program.programId },
      });
    },
    onError: () =>
      toast.error("Program başlatılamadı. Tekrar deneyebilirsiniz."),
  });

  const handleAssign = () => {
    if (!session?.user) {
      toast.info("Programı başlatmak için lütfen giriş yapın.");
      void navigate({
        to: "/password/login",
        search: { redirect: "/program" },
      });
      return;
    }
    assignMutation.mutate({
      data: {
        programId: program.programId,
      },
    });
  };

  const discountPercent =
    !program.priceKurus &&
    program.price &&
    program.oldPrice &&
    program.oldPrice > program.price
      ? Math.round(
          ((program.oldPrice - program.price) / program.oldPrice) * 100,
        )
      : null;

  return (
    <div
      className={cn(
        "group flex flex-col overflow-hidden transition-all duration-300",
        isCurrentProgram && "rounded-3xl bg-secondary/30",
        !isCurrentProgram &&
          "rounded-2xl border border-transparent hover:bg-muted/30",
      )}
    >
      <div className="flex h-full flex-col">
        {program.image && (
          <div className="aspect-video w-full overflow-hidden rounded-xl border border-border/40 bg-muted/20">
            <img
              src={program.image}
              alt={effectiveName}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col p-6">
          <div className="mb-6 flex-1 space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-bold text-2xl tracking-tight">
                  {effectiveName}
                </h3>
                <Badge variant="secondary">
                  {program.difficulty <= 2
                    ? "Başlangıç"
                    : program.difficulty === 3
                      ? "Orta"
                      : "İleri"}
                </Badge>
                {!isPaidProgram && <Badge variant="outline">Ücretsiz</Badge>}
                {isPaidProgram && (
                  <Badge
                    variant="outline"
                    className="border-primary/20 bg-primary/5 font-bold text-[10px] text-primary uppercase"
                  >
                    <Lock className="mr-1 h-3 w-3" /> Ücretli
                  </Badge>
                )}
                {discountPercent !== null && (
                  <Badge className="border-emerald-500/20 bg-emerald-500/10 font-bold text-[10px] text-emerald-600 dark:text-emerald-400">
                    %{discountPercent} İndirim
                  </Badge>
                )}
              </div>
              {isCurrentProgram && (
                <p className="font-bold text-primary text-xs uppercase tracking-widest">
                  Şu an çalıştığın program
                </p>
              )}
            </div>

            {/* Price display for paid programs */}
            {isPaidProgram && displayPriceTl && (
              <div className="flex items-baseline gap-2.5 pt-1">
                <span className="font-black text-2xl text-primary tracking-tight">
                  ₺{displayPriceTl.toLocaleString("tr-TR")}
                </span>
                {!program.priceKurus &&
                  program.oldPrice &&
                  program.oldPrice > displayPriceTl && (
                    <span className="font-semibold text-muted-foreground/60 text-sm line-through">
                      ₺{program.oldPrice.toLocaleString("tr-TR")}
                    </span>
                  )}
                {hasAccess && (
                  <Badge
                    variant="outline"
                    className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 text-xs dark:text-emerald-400"
                  >
                    Satın Alındı
                  </Badge>
                )}
              </div>
            )}

            {effectiveDescription && (
              <p className="line-clamp-2 text-muted-foreground/80 text-sm leading-relaxed">
                {effectiveDescription}
              </p>
            )}

            <div className="flex items-center gap-4 font-bold text-muted-foreground/60 text-xs uppercase tracking-widest">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {program.durationWeeks} Hafta
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {formatDaysPerWeekShort(
                  program.daysPerWeek,
                  program.maxDaysPerWeek,
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {isCurrentProgram && !needsPurchase ? (
              <Button
                asChild
                className="h-11 min-w-[120px] flex-1 rounded-xl shadow-none"
              >
                <Link
                  to="/program/$programId"
                  params={{ programId: program.programId }}
                >
                  Devam Et
                </Link>
              </Button>
            ) : needsPurchase ? (
              <Button
                asChild
                className="h-11 min-w-[120px] flex-1 rounded-xl font-bold shadow-none"
              >
                <Link
                  role="button"
                  to="/program/$programId"
                  params={{ programId: program.programId }}
                >
                  <ShoppingBag className="mr-1.5 h-4 w-4" />
                  İncele & Satın Al
                </Link>
              </Button>
            ) : (
              <ProgramActivationWizard
                programName={effectiveName}
                isPending={assignMutation.isPending}
                onConfirm={handleAssign}
                hasActiveProgram={Boolean(activeProgram)}
              >
                <Button
                  disabled={assignMutation.isPending}
                  className="h-11 min-w-[120px] flex-1 rounded-xl shadow-none"
                >
                  {assignMutation.isPending ? "..." : "Programı Başlat"}
                </Button>
              </ProgramActivationWizard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
